/**
 * SearchForm - 図書館統合検索のフリーワード入力フォーム
 */

import { ScanBarcode, Search } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useLibrarySearchStore } from '@/store/useLibrarySearchStore'

import { isAmazonUrl } from '../lib/amazon-url'
import { BarcodeScannerSheet } from './BarcodeScannerSheet'

type Props = {
  /** 検索実行時に呼ばれる (お気に入りタブ表示中でも検索結果タブへ移動させる用途) */
  onSearch?: () => void
  /** 検索欄に Amazon の URL が入力されたときに、検索の代わりに呼ばれる */
  onAmazonUrl?: (url: string) => void
}

export const SearchForm = ({ onSearch, onAmazonUrl }: Props) => {
  const initialQuery = useLibrarySearchStore((s) => s.query)
  const startSearch = useLibrarySearchStore((s) => s.startSearch)
  const status = useLibrarySearchStore((s) => s.status)

  const [value, setValue] = useState(initialQuery)
  const [scannerOpen, setScannerOpen] = useState(false)

  const isBusy = status === 'loading' || status === 'polling'

  const handleSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!value.trim()) return
    if (onAmazonUrl && isAmazonUrl(value)) {
      onAmazonUrl(value.trim())
      setValue('')
      return
    }
    onSearch?.()
    void startSearch(value)
  }

  // 読み取った ISBN はそのまま Calil の検索語として使える
  const handleDetected = (isbn: string) => {
    setScannerOpen(false)
    setValue(isbn)
    onSearch?.()
    void startSearch(isbn)
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="タイトル / 著者 / ISBN"
          className="flex-1"
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setScannerOpen(true)}
          aria-label="バーコードで検索"
        >
          <ScanBarcode className="size-4" />
        </Button>
        <Button type="submit" size="icon" disabled={!value.trim() || isBusy} aria-label="検索">
          <Search className="size-4" />
        </Button>
      </form>

      <BarcodeScannerSheet open={scannerOpen} onOpenChange={setScannerOpen} onDetected={handleDetected} />
    </>
  )
}
